import React, { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import { 
  GlobeAltIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ServerIcon
} from '@heroicons/react/24/outline';
import { dockerAPI } from '../services/api';

const Traefik = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedStacks, setExpandedStacks] = useState(new Set());

  // Query services with Traefik labels
  const { data: traefikData } = useQuery(
    'traefik-services',
    dockerAPI.getTraefikServices,
    { refetchInterval: 15000 }
  );

  const traefikServices = traefikData?.data?.services || [];

  // Filter services based on search and status
  const filteredServices = traefikServices.filter(service => {
    const matchesSearch = service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         service.serviceName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         service.domain.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'healthy' && service.health === 'healthy') ||
                         (statusFilter === 'unhealthy' && service.health === 'unhealthy');
    
    return matchesSearch && matchesStatus;
  });

  // Group services by stack
  const servicesByStack = useMemo(() => {
    const grouped = filteredServices.reduce((acc, service) => {
      const stack = service.stack || 'Sem Stack';
      if (!acc[stack]) {
        acc[stack] = [];
      }
      acc[stack].push(service);
      return acc;
    }, {});

    // Sort stacks alphabetically and services within each stack
    const sortedStacks = {};
    Object.keys(grouped)
      .sort()
      .forEach(stack => {
        sortedStacks[stack] = grouped[stack].sort((a, b) => 
          a.serviceName.localeCompare(b.serviceName)
        );
      });

    return sortedStacks;
  }, [filteredServices]);

  const toggleStack = (stack) => {
    const newExpanded = new Set(expandedStacks);
    if (newExpanded.has(stack)) {
      newExpanded.delete(stack);
    } else {
      newExpanded.add(stack);
    }
    setExpandedStacks(newExpanded);
  };

  const expandAll = () => {
    setExpandedStacks(new Set(Object.keys(servicesByStack)));
  };

  const collapseAll = () => {
    setExpandedStacks(new Set());
  };

  const getStatusColor = (health) => {
    switch (health) {
      case 'healthy': return 'text-green-600';
      case 'unhealthy': return 'text-red-600';
      default: return 'text-yellow-600';
    }
  };

  const getStatusIcon = (health) => {
    switch (health) {
      case 'healthy': return CheckCircleIcon;
      case 'unhealthy': return ExclamationTriangleIcon;
      default: return ClockIcon;
    }
  };

  const ServiceRow = ({ service }) => {
    const StatusIcon = getStatusIcon(service.health);
    
    return (
      <tr className="hover:bg-gray-50">
        <td className="px-4 py-3 text-sm">
          <div className="flex items-center space-x-2">
            <StatusIcon className={`h-4 w-4 ${getStatusColor(service.health)}`} />
            <span className={`font-medium ${getStatusColor(service.health)}`}>
              {service.health}
            </span>
          </div>
        </td>
        <td className="px-4 py-3 text-sm font-medium text-gray-900">
          <div className="max-w-xs truncate" title={service.name}>
            {service.serviceName}
          </div>
        </td>
        <td className="px-4 py-3 text-sm text-gray-600">
          <div className="max-w-sm truncate" title={service.domain}>
            {service.domain}
          </div>
        </td>
        <td className="px-4 py-3 text-sm text-gray-600">
          <div className="max-w-sm truncate" title={service.image}>
            {service.image}
          </div>
        </td>
        <td className="px-4 py-3 text-sm text-gray-600">
          {service.ports || 'N/A'}
        </td>
        <td className="px-4 py-3 text-right">
          <div className="flex justify-end space-x-2">
            {service.url ? (
              <a
                href={service.url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary text-xs py-1 px-2 flex items-center space-x-1"
                title="Abrir URL"
              >
                <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                <span>Abrir</span>
              </a>
            ) : (
              <span className="text-xs text-gray-400 py-1 px-2">
                Indisponível
              </span>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Traefik Proxy</h1>
        <p className="mt-1 text-sm text-gray-500">
          Visualize serviços expostos através do Traefik
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircleIcon className="h-8 w-8 text-green-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-green-600">Serviços Ativos</p>
              <p className="text-2xl font-bold text-green-900">
                {filteredServices.filter(s => s.health === 'healthy').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-red-600">Serviços Inativos</p>
              <p className="text-2xl font-bold text-red-900">
                {filteredServices.filter(s => s.health === 'unhealthy').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center">
            <ServerIcon className="h-8 w-8 text-purple-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-purple-600">Stacks</p>
              <p className="text-2xl font-bold text-purple-900">
                {Object.keys(servicesByStack).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <GlobeAltIcon className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-blue-600">Total</p>
              <p className="text-2xl font-bold text-blue-900">
                {filteredServices.length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex items-center space-x-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buscar:
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Nome do serviço ou domínio..."
                className="input text-sm w-64"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status:
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="input text-sm"
              >
                <option value="all">Todos</option>
                <option value="healthy">Ativos</option>
                <option value="unhealthy">Inativos</option>
              </select>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-600">
              {filteredServices.length} de {traefikServices.length} serviço(s)
            </div>
            <div className="flex space-x-2">
              <button
                onClick={expandAll}
                className="btn-secondary text-xs py-1 px-2"
              >
                Expandir Tudo
              </button>
              <button
                onClick={collapseAll}
                className="btn-secondary text-xs py-1 px-2"
              >
                Colapsar Tudo
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Services by Stack */}
      <div className="space-y-4">
        {Object.keys(servicesByStack).length === 0 ? (
          <div className="card">
            <div className="text-center py-8">
              <GlobeAltIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                Nenhum serviço encontrado
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Tente ajustar os filtros de busca.'
                  : 'Não há serviços Traefik configurados no momento.'
                }
              </p>
            </div>
          </div>
        ) : (
          Object.entries(servicesByStack).map(([stack, services]) => (
            <div key={stack} className="card">
              {/* Stack Header */}
              <div 
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 border-b"
                onClick={() => toggleStack(stack)}
              >
                <div className="flex items-center space-x-3">
                  {expandedStacks.has(stack) ? (
                    <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronRightIcon className="h-5 w-5 text-gray-500" />
                  )}
                  <ServerIcon className="h-5 w-5 text-purple-600" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{stack}</h3>
                    <p className="text-sm text-gray-500">
                      {services.length} serviço(s) • {services.filter(s => s.health === 'healthy').length} ativo(s)
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs font-medium">
                    {services.length}
                  </span>
                </div>
              </div>

              {/* Services in Stack */}
              {expandedStacks.has(stack) && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Serviço</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Domínio</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Imagem</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Portas</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {services.map((service) => (
                        <ServiceRow key={service.id} service={service} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Note */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <ShieldCheckIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-2" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">Nota sobre Traefik</p>
            <p className="mt-1">
              Esta página lista serviços baseados em containers ativos com portas expostas. 
              Para configuração completa do Traefik, verifique as labels dos containers e 
              o arquivo de configuração do Traefik.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Traefik;